import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm border border-nude bg-card p-10"
        data-testid="admin-login-form"
      >
        <p className="overline-label text-bronze">Leticia &amp; Nick</p>
        <h1 className="font-serif text-3xl font-light text-ink mt-4 tracking-tight">
          Panel de invitados
        </h1>
        <div className="mt-8 space-y-5">
          <div>
            <Label htmlFor="admin-email" className="overline-label text-taupe">Correo</Label>
            <Input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 border-nude bg-cream"
              data-testid="admin-login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="overline-label text-taupe">Contraseña</Label>
            <Input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 border-nude bg-cream"
              data-testid="admin-login-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-ink text-cream hover:bg-olive rounded-none tracking-[0.25em] uppercase text-xs"
            data-testid="admin-login-submit-button"
          >
            {loading ? "Entrando…" : "Entrar"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Sin caracteres que se confundan al dictar un enlace por teléfono: fuera la
// o y la ele minúsculas, y el cero y el uno. Son 32 justos, así que el resto
// del byte reparte por igual y no sesga hacia las primeras letras.
const SLUG_ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";

function randomSlug(length = 7) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => SLUG_ALPHABET[b % SLUG_ALPHABET.length]).join("");
}

function FamilyDialog({ open, onClose, onSave, editing }) {
  const [name, setName] = useState("");
  const [passes, setPasses] = useState(2);
  const [slug, setSlug] = useState("");

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setPasses(editing?.passes || 2);
      setSlug(editing?.slug || "");
    }
  }, [open, editing]);

  const submit = (e) => {
    e.preventDefault();
    onSave({ name: name.trim(), passes: Number(passes), slug: slug.trim() || null });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-cream border-nude" data-testid="family-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-ink">
            {editing ? "Editar familia" : "Nueva familia"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div>
            <Label htmlFor="family-name" className="overline-label text-taupe">Nombre de la familia</Label>
            <Input
              id="family-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Familia López"
              className="mt-2 border-nude bg-card"
              data-testid="family-name-input"
            />
          </div>
          <div>
            <Label htmlFor="family-passes" className="overline-label text-taupe">Número de pases</Label>
            <Input
              id="family-passes"
              type="number"
              min={1}
              max={50}
              required
              value={passes}
              onChange={(e) => setPasses(e.target.value)}
              className="mt-2 border-nude bg-card"
              data-testid="family-passes-input"
            />
          </div>
          <div>
            <Label htmlFor="family-slug" className="overline-label text-taupe">
              Enlace
            </Label>
            <div className="mt-2 flex gap-2">
              <Input
                id="family-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="5"
                className="border-nude bg-card"
                data-testid="family-slug-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setSlug(randomSlug())}
                className="shrink-0 rounded-none border-nude text-taupe hover:bg-sand hover:text-ink tracking-[0.2em] uppercase text-[0.65rem]"
                data-testid="random-slug-button"
              >
                Autoasignar
              </Button>
            </div>
            <p className="mt-2 text-xs text-taupe leading-relaxed">
              Déjalo vacío y se numera solo (1, 2, 3). Con Autoasignar sale un
              código imposible de adivinar, para que nadie llegue a la invitación
              de otra familia probando números.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              className="bg-ink text-cream hover:bg-olive rounded-none tracking-[0.25em] uppercase text-xs"
              data-testid="save-family-button"
            >
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Declarado aquí fuera a propósito. Dentro de SettingsForm, cada pulsación
// creaba una función nueva, React lo trataba como otro componente, desmontaba
// el input y el foco saltaba tras cada letra.
function Field({ id, label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label htmlFor={id} className="overline-label text-taupe">{label}</Label>
      <Input
        id={id}
        type={type}
        required
        value={value}
        onChange={onChange}
        className="mt-2 border-nude bg-card"
        data-testid={`${id}-input`}
      />
    </div>
  );
}

function SettingsForm({ headers }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/settings`, { headers })
      .then((res) => setForm(res.data))
      .catch(() => toast.error("No se pudieron cargar los datos"));
  }, []);

  if (!form) {
    return (
      <p className="py-12 text-center text-sm text-taupe" data-testid="settings-loading">
        Cargando datos de la boda…
      </p>
    );
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVenue = (key, field) => (e) =>
    setForm((f) => ({ ...f, [key]: { ...f[key], [field]: e.target.value } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        date_iso: form.date_iso.length === 16 ? `${form.date_iso}:00` : form.date_iso,
      };
      await axios.put(`${API}/admin/settings`, payload, { headers });
      toast.success("Datos de la boda actualizados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="border border-nude bg-card p-8 sm:p-10"
      data-testid="settings-form"
    >
      <p className="overline-label text-bronze">Los novios</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="settings-bride" label="Nombre novia" value={form.bride} onChange={set("bride")} />
        <Field id="settings-groom" label="Nombre novio" value={form.groom} onChange={set("groom")} />
      </div>

      <p className="overline-label text-bronze mt-10">Fecha y hora</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="settings-date" label="Fecha (para la cuenta regresiva)" type="datetime-local" value={form.date_iso.slice(0, 16)} onChange={set("date_iso")} />
        <Field id="settings-datelabel" label="Texto de la fecha" value={form.date_label} onChange={set("date_label")} />
        <Field id="settings-time" label="Texto de la hora" value={form.time_label} onChange={set("time_label")} />
        <Field id="settings-deadline" label="Fecha límite de confirmación" value={form.rsvp_deadline} onChange={set("rsvp_deadline")} />
      </div>

      <p className="overline-label text-bronze mt-10">Ceremonia</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="settings-ceremony-venue" label="Lugar" value={form.ceremony.venue} onChange={setVenue("ceremony", "venue")} />
        <Field id="settings-ceremony-address" label="Dirección" value={form.ceremony.address} onChange={setVenue("ceremony", "address")} />
        <div className="sm:col-span-2">
          <Field id="settings-ceremony-maps" label="Enlace de Google Maps" value={form.ceremony.maps_url} onChange={setVenue("ceremony", "maps_url")} />
        </div>
      </div>

      <p className="overline-label text-bronze mt-10">Celebración</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="settings-celebration-venue" label="Lugar" value={form.celebration.venue} onChange={setVenue("celebration", "venue")} />
        <Field id="settings-celebration-address" label="Dirección" value={form.celebration.address} onChange={setVenue("celebration", "address")} />
        <div className="sm:col-span-2">
          <Field id="settings-celebration-maps" label="Enlace de Google Maps" value={form.celebration.maps_url} onChange={setVenue("celebration", "maps_url")} />
        </div>
      </div>

      <p className="overline-label text-bronze mt-10">Contacto y enlaces</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field id="settings-whatsapp" label="Número de WhatsApp (solo números, con código país)" value={form.whatsapp} onChange={set("whatsapp")} />
        <Field id="settings-openurl" label="Enlace del botón “Abrir invitación”" value={form.open_url} onChange={set("open_url")} />
      </div>

      <Button
        type="submit"
        disabled={saving}
        className="mt-10 bg-ink text-cream hover:bg-olive rounded-none tracking-[0.25em] uppercase text-xs"
        data-testid="settings-save-button"
      >
        {saving ? "Guardando…" : "Guardar cambios"}
      </Button>
    </form>
  );
}

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" });

const RsvpBadge = ({ attending }) =>
  attending ? (
    <Badge className="bg-olive text-cream rounded-none font-normal">Sí asisten</Badge>
  ) : (
    <Badge className="bg-destructive/10 text-destructive border border-destructive/30 rounded-none font-normal">No asisten</Badge>
  );

// Tres estados. "Sin confirmar" borra la respuesta, así que la familia vuelve
// a contar como pendiente y puede responder ella misma desde su enlace.
const RSVP_OPTIONS = [
  { value: "pendiente", label: "Sin confirmar" },
  { value: "si", label: "Sí asisten" },
  { value: "no", label: "No asisten" },
];

const rsvpToValue = (rsvp) => (!rsvp ? "pendiente" : rsvp.attending ? "si" : "no");
const valueToAttending = (v) => (v === "pendiente" ? null : v === "si");

function RsvpSelect({ slug, rsvp, onChange, disabled }) {
  return (
    <Select value={rsvpToValue(rsvp)} onValueChange={(v) => onChange(slug, valueToAttending(v))} disabled={disabled}>
      <SelectTrigger
        className="h-9 w-[10.5rem] rounded-none border-nude bg-card text-xs"
        data-testid={`rsvp-select-${slug}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-none border-nude bg-cream">
        {RSVP_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-xs">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


const TEXT_FIELDS = [
  ["Sobre", [
    ["hero_overline", "Rótulo de arriba"],
    ["hero_hint", "Aviso para abrir el sobre"],
    ["hero_lead", "Frase de la invitación"],
    ["hero_passes", "Antes del número de pases"],
    ["hero_cta", "Texto del botón"],
    ["hero_scroll", "Aviso para bajar"],
  ]],
  ["Cinta que cruza la invitación", [
    ["marquee_date", "Fecha (vacío = se calcula de la fecha de la boda)"],
    ["marquee_word_1", "Primera frase"],
    ["marquee_word_2", "Segunda frase"],
  ]],
  ["Sección de presentación", [
    ["quote_overline", "Rótulo"],
    ["quote_title", "Titular"],
    ["quote_accent", "Última palabra del titular (en cursiva)"],
    ["quote_body", "Párrafo"],
    ["quote_cite", "Frase en cursiva"],
  ]],
];

const PHOTO_FIELDS = [
  ["couple", "Foto de la pareja"],
  ["venue", "Foto del lugar"],
];

function PhotoField({ nombre, etiqueta, url, headers, onSubido }) {
  const [subiendo, setSubiendo] = useState(false);
  const inputId = `foto-${nombre}`;

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("La imagen pesa más de 6 MB");
      return;
    }
    setSubiendo(true);
    try {
      const { data } = await axios.post(`${API}/admin/media/${nombre}`, file, {
        headers: { ...headers, "Content-Type": file.type },
      });
      onSubido(nombre, data.url);
      toast.success("Foto actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la foto");
    } finally {
      setSubiendo(false);
    }
  };

  const src = url ? (url.startsWith("http") ? url : process.env.REACT_APP_BACKEND_URL + url) : "";

  return (
    <div className="border border-nude bg-cream p-4">
      <p className="overline-label text-taupe">{etiqueta}</p>
      <div className="mt-3 flex items-center gap-4">
        <div className="h-24 w-24 shrink-0 overflow-hidden border border-nude bg-sand">
          {src ? (
            <img src={src} alt="" className="h-full w-full object-cover" data-testid={`preview-${nombre}`} />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-[0.6rem] uppercase tracking-[0.2em] text-taupe">
              Sin foto
            </span>
          )}
        </div>
        <div>
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={subir}
            className="hidden"
            data-testid={`input-${nombre}`}
          />
          <Label
            htmlFor={inputId}
            className="inline-block cursor-pointer border border-nude bg-card px-4 py-2 text-[0.65rem] uppercase tracking-[0.2em] text-taupe hover:bg-sand hover:text-ink"
          >
            {subiendo ? "Subiendo…" : url ? "Cambiar foto" : "Subir foto"}
          </Label>
          <p className="mt-2 text-xs text-taupe">JPG, PNG o WebP. Hasta 6 MB.</p>
        </div>
      </div>
    </div>
  );
}

function ContentForm({ headers }) {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/content`, { headers })
      .then((res) => setContent(res.data))
      .catch(() => toast.error("No se pudo cargar el contenido"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!content) {
    return (
      <p className="py-12 text-center text-sm text-taupe" data-testid="content-loading">
        Cargando contenido…
      </p>
    );
  }

  const setText = (key) => (e) =>
    setContent((c) => ({ ...c, texts: { ...c.texts, [key]: e.target.value } }));

  const onSubido = (nombre, url) =>
    setContent((c) => ({ ...c, photos: { ...c.photos, [nombre]: url } }));

  // Se guarda al marcarlo y manda solo la opción: los textos a medio escribir
  // no viajan, así que no se publican por accidente.
  const toggleOnlyEnvelope = async (checked) => {
    const previo = Boolean(content.options?.only_envelope);
    setContent((c) => ({ ...c, options: { ...c.options, only_envelope: checked } }));
    try {
      await axios.put(`${API}/admin/content`, { options: { only_envelope: checked } }, { headers });
      toast.success(checked ? "La invitación muestra solo el sobre" : "La invitación muestra todas las secciones");
    } catch {
      setContent((c) => ({ ...c, options: { ...c.options, only_envelope: previo } }));
      toast.error("No se pudo guardar el cambio");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, content, { headers });
      toast.success("Contenido actualizado");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-nude bg-card p-6 sm:p-10" data-testid="content-form">
      <div className="mb-10 flex items-start justify-between gap-6 border border-nude bg-cream p-4">
        <div>
          <Label htmlFor="only-envelope" className="overline-label text-bronze">
            Mostrar solo el sobre
          </Label>
          <p className="mt-2 text-xs text-taupe leading-relaxed">
            Al marcarlo, la invitación enseña únicamente el sobre de la portada y oculta la
            presentación, la cinta, la cuenta regresiva, los lugares y la confirmación. Se
            guarda al momento.
          </p>
        </div>
        <Switch
          id="only-envelope"
          checked={Boolean(content.options?.only_envelope)}
          onCheckedChange={toggleOnlyEnvelope}
          className="shrink-0 data-[state=checked]:bg-olive"
          data-testid="only-envelope-switch"
        />
      </div>

      <p className="overline-label text-bronze">Fotos</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
        {PHOTO_FIELDS.map(([nombre, etiqueta]) => (
          <PhotoField
            key={nombre}
            nombre={nombre}
            etiqueta={etiqueta}
            url={content.photos[nombre]}
            headers={headers}
            onSubido={onSubido}
          />
        ))}
      </div>
      <p className="mt-4 text-xs text-taupe leading-relaxed">
        La foto se guarda al subirla, sin esperar al botón. Los textos sí necesitan Guardar.
      </p>

      {TEXT_FIELDS.map(([grupo, campos]) => (
        <div key={grupo}>
          <p className="overline-label text-bronze mt-10">{grupo}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {campos.map(([key, etiqueta]) => (
              <div key={key} className={key === "quote_body" ? "sm:col-span-2" : ""}>
                <Label htmlFor={`texto-${key}`} className="overline-label text-taupe">
                  {etiqueta}
                </Label>
                {key === "quote_body" ? (
                  <textarea
                    id={`texto-${key}`}
                    rows={3}
                    value={content.texts[key]}
                    onChange={setText(key)}
                    className="mt-2 w-full resize-y border border-nude bg-card px-3 py-2 text-sm text-ink outline-none focus:border-bronze"
                    data-testid={`texto-${key}-input`}
                  />
                ) : (
                  <Input
                    id={`texto-${key}`}
                    value={content.texts[key]}
                    onChange={setText(key)}
                    className="mt-2 border-nude bg-card"
                    data-testid={`texto-${key}-input`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-10">
        <Button
          type="submit"
          disabled={saving}
          className="bg-ink text-cream hover:bg-olive rounded-none tracking-[0.25em] uppercase text-xs"
          data-testid="save-content-button"
        >
          {saving ? "Guardando…" : "Guardar textos"}
        </Button>
      </div>
    </form>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [families, setFamilies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmSlug, setConfirmSlug] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [savingRsvp, setSavingRsvp] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [famRes, rsvpRes] = await Promise.all([
        axios.get(`${API}/admin/families`, { headers }),
        axios.get(`${API}/admin/rsvps`, { headers }),
      ]);
      setFamilies(famRes.data);
      setRsvps(rsvpRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken("");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const save = async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/families/${editing.slug}`, payload, { headers });
        toast.success("Familia actualizada");
      } else {
        await axios.post(`${API}/admin/families`, payload, { headers });
        toast.success("Familia creada");
      }
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (slug) => {
    if (confirmSlug !== slug) {
      setConfirmSlug(slug);
      setTimeout(() => setConfirmSlug(null), 3000);
      return;
    }
    await axios.delete(`${API}/admin/families/${slug}`, { headers });
    toast.success("Familia eliminada");
    setConfirmSlug(null);
    load();
  };

  const setRsvp = async (slug, attending) => {
    setSavingRsvp(slug);
    try {
      await axios.put(`${API}/admin/rsvps/${slug}`, { attending }, { headers });
      await load();
      toast.success(
        attending === null
          ? "Marcada como sin confirmar"
          : attending
            ? "Marcada como asisten"
            : "Marcada como no asisten"
      );
    } catch {
      toast.error("No se pudo guardar la respuesta");
    } finally {
      setSavingRsvp(null);
    }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
    toast.success("Enlace copiado");
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
  };

  if (!token) return <LoginForm onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-cream px-6 sm:px-12 py-12" data-testid="admin-panel">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-nude pb-8">
          <div>
            <p className="overline-label text-bronze">Leticia &amp; Nick</p>
            <h1 className="font-serif text-4xl font-light text-ink mt-3 tracking-tight">
              Panel de invitados
            </h1>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={logout}
              className="border-nude text-taupe hover:bg-sand rounded-none tracking-[0.2em] uppercase text-xs"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Salir
            </Button>
          </div>
        </div>

        <Tabs defaultValue="familias" className="mt-10">
          <TabsList className="bg-sand border border-nude rounded-none">
            <TabsTrigger value="familias" className="rounded-none text-xs uppercase tracking-[0.2em]" data-testid="tab-familias">
              Familias
            </TabsTrigger>
            <TabsTrigger value="confirmaciones" className="rounded-none text-xs uppercase tracking-[0.2em]" data-testid="tab-confirmaciones">
              Confirmaciones
            </TabsTrigger>
            <TabsTrigger value="contenido" className="rounded-none text-xs uppercase tracking-[0.2em]" data-testid="tab-contenido">
              Contenido
            </TabsTrigger>
            <TabsTrigger value="boda" className="rounded-none text-xs uppercase tracking-[0.2em]" data-testid="tab-boda">
              Datos de la boda
            </TabsTrigger>
          </TabsList>

          <TabsContent value="familias">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                className="bg-ink text-cream hover:bg-olive rounded-none tracking-[0.2em] uppercase text-xs"
                data-testid="add-family-button"
              >
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nueva familia
              </Button>
            </div>
            <div className="border border-nude bg-card overflow-x-auto" data-testid="families-table">
              <Table className="min-w-[38rem]">
                <TableHeader>
                  <TableRow className="border-nude">
                    <TableHead className="overline-label text-taupe">Enlace</TableHead>
                    <TableHead className="overline-label text-taupe">Familia</TableHead>
                    <TableHead className="overline-label text-taupe">Pases</TableHead>
                    <TableHead className="overline-label text-taupe">Confirmación</TableHead>
                    <TableHead className="overline-label text-taupe text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {families.map((f) => {
                    const rsvp = rsvps.find((r) => r.slug === f.slug);
                    return (
                      <TableRow key={f.slug} className="border-nude" data-testid={`family-row-${f.slug}`}>
                        <TableCell>
                          <button
                            onClick={() => copyLink(f.slug)}
                            className="group inline-flex items-center gap-2 font-mono text-sm text-olive hover:text-ink"
                            data-testid={`copy-link-${f.slug}`}
                          >
                            /{f.slug}
                            <Copy className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                          </button>
                        </TableCell>
                        <TableCell className="font-serif text-lg text-ink">{f.name}</TableCell>
                        <TableCell className="text-sm text-taupe" data-testid={`passes-${f.slug}`}>
                          {f.passes}
                        </TableCell>
                        <TableCell data-testid={`rsvp-status-${f.slug}`}>
                          <RsvpSelect
                            slug={f.slug}
                            rsvp={rsvp}
                            onChange={setRsvp}
                            disabled={savingRsvp === f.slug}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => { setEditing(f); setDialogOpen(true); }}
                              className="p-2 text-taupe hover:text-ink transition-colors"
                              data-testid={`edit-family-${f.slug}`}
                              aria-label="Editar"
                            >
                              <Pencil className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => remove(f.slug)}
                              className={`p-2 transition-colors ${confirmSlug === f.slug ? "text-destructive" : "text-taupe hover:text-destructive"}`}
                              data-testid={`delete-family-${f.slug}`}
                              aria-label="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {families.length === 0 && (
                    <TableRow className="border-nude">
                      <TableCell colSpan={5} className="py-12 text-center text-sm text-taupe" data-testid="families-empty">
                        Aún no hay familias. Crea la primera con “Nueva familia”.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="mt-6 text-xs text-taupe leading-relaxed">
              Haz clic en un enlace para copiarlo y compartirlo con cada familia. Al abrirlo verán su
              nombre y sus pases asignados.
            </p>
          </TabsContent>

          <TabsContent value="confirmaciones">
            <div className="border border-nude bg-card overflow-x-auto" data-testid="rsvps-table">
              <div className="flex flex-wrap gap-6 border-b border-nude px-6 py-5" data-testid="rsvps-summary">
                <span className="text-xs uppercase tracking-[0.2em] text-olive">
                  {rsvps.filter((r) => r.attending).length} confirmadas
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-destructive">
                  {rsvps.filter((r) => !r.attending).length} no asisten
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-taupe">
                  {families.filter((f) => !rsvps.some((r) => r.slug === f.slug)).length} pendientes
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-nude">
                    <TableHead className="overline-label text-taupe">Familia</TableHead>
                    <TableHead className="overline-label text-taupe">Pases</TableHead>
                    <TableHead className="overline-label text-taupe">Respuesta</TableHead>
                    <TableHead className="overline-label text-taupe text-right">Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rsvps.map((r) => (
                    <TableRow key={r.slug} className="border-nude" data-testid={`rsvp-row-${r.slug}`}>
                      <TableCell className="font-serif text-lg text-ink">{r.family_name}</TableCell>
                      <TableCell className="text-sm text-taupe">{r.passes}</TableCell>
                      <TableCell><RsvpBadge attending={r.attending} /></TableCell>
                      <TableCell className="text-right text-sm text-taupe">{formatDate(r.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                  {rsvps.length === 0 && (
                    <TableRow className="border-nude">
                      <TableCell colSpan={4} className="py-12 text-center text-sm text-taupe" data-testid="rsvps-empty">
                        Aún no hay confirmaciones. Aparecerán aquí cuando las familias respondan.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="contenido">
            <ContentForm headers={headers} />
          </TabsContent>

          <TabsContent value="boda">
            <SettingsForm headers={headers} />
          </TabsContent>
        </Tabs>
      </div>

      <FamilyDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={save}
        editing={editing}
      />
    </div>
  );
}
